// Translation utilities for multi-language support

export interface Translations {
  featuredOffers: string;
  featuredOffersDescription: string;
  exclusiveOffersTitle: string;
  exclusiveOffersDescription: string;
  howToGetGift: string;
  step1Title: string;
  step1Description: string;
  step2Title: string;
  step2Description: string;
  step3Title: string;
  step3Description: string;
  filterByCategories: string;
  swipeHint: string;
  clearFilter: string;
  categories: string;
  validUntil: string;
  getPromoCode: string;
  requestCode: string;
  cancel: string;
  emailAddress: string;
  enterYourEmail: string;
  agreeWithPrivacy: string;
  privacyPolicy: string;
  platformPrivacyPolicy: string;
  partnerPrivacyPolicy: string;
  and: string;
  promoCodeRequested: string;
  yourPromocode: string;
  yourPromocodeLabel: string;
  promocodeDescription: string;
  promocodeTipTitle: string;
  promocodeTipDescription: string;
  copyCode: string;
  copied: string;
  close: string;
  visitPartnerWebsite: string;
  useYourCode: string;
  visitPartnerPage: string;
  apiRequestSentTitle: string;
  apiRequestSentDescription: string;
  apiRequestSentNoteTitle: string;
  apiRequestSentNoteDescription: string;
  apiRequestSentButton: string;
  termsOfService: string;
  contactUs: string;
  poweredBy: string;
  campaign: string;
  region: string;
  noActiveOffers: string;
  noActiveOffersDescription: string;
  campaignNotFound: string;
  campaignNotFoundDescription: string;
  loadingCampaign: string;
  redirectDescription: string;
  directContactNotification: string;
  directContactTitle: string;
  directContactConfirmQuestion: string;
  redirectConfirmTitle: string;
  redirectConfirmMessage: string;
  redirectConfirmQuestion: string;
  proceed: string;
  promoSentEmailTitle: string;
  promoSentEmailDescription: string; // include {email}
  promoEmailReminderMessage: string;
  privacyConsentReminderTitle: string;
  privacyConsentReminderMessage: string;
  invalidEmailTitle: string;
  invalidEmailMessage: string;
  missingEmailTitle: string;
  missingEmailMessage: string;
  prefetchBadge: string;
  prefetchTitle: string;
  prefetchOffersReady: string;
  prefetchOffersPending: string;
  prefetchProgressLabel: string; // use {percent}
  prefetchButtonReady: string;
  prefetchButtonPending: string;
  // Newsletter subscription
  newsletterTitle: string;
  newsletterPlaceholder: string;
  newsletterPrivacyLabel: string;
  newsletterSubscribe: string;
  newsletterSuccess: string;
  newsletterError: string;
  // Email-specific translations
  emailPromoTitle: string;
  emailThankYouForInterest: string;
  emailUsePromocodeBelow: string;
  emailYourPromoCode: string;
  emailVisitPartnerPage: string;
  emailUseYourCode: string;
  emailVisitPartnerNote: string;
}

export const translations: Record<string, Translations> = {
  // English (default)
  'en': {
    featuredOffers: 'Featured Offers',
    featuredOffersDescription: 'Discover amazing deals and offers from our trusted partners',
    exclusiveOffersTitle: 'Your Exclusive Offers Await',
    exclusiveOffersDescription: 'Click on any offer below to claim your special gift from our trusted partners',
    howToGetGift: 'How to Get Your Gift - 3 Easy Steps',
    step1Title: 'Choose Offer',
    step1Description: 'Browse and select your favorite deal',
    step2Title: 'Enter Email',
    step2Description: 'Provide your email address',
    step3Title: 'Get Code',
    step3Description: 'Receive your promo code instantly',
    filterByCategories: 'Filter by Categories',
    swipeHint: '← Swipe to see more →',
    clearFilter: 'Clear Filter',
    categories: 'Categories:',
    validUntil: 'Valid until',
    getPromoCode: 'Get Promo Code',
    requestCode: 'Request Code',
    cancel: 'Cancel',
    emailAddress: 'Email Address',
    enterYourEmail: 'Enter your email',
    agreeWithPrivacy: 'I agree with',
    privacyPolicy: 'Privacy Policy',
    platformPrivacyPolicy: 'Platform Privacy Policy',
    partnerPrivacyPolicy: 'Partner Privacy Policy',
    and: 'and',
    promoCodeRequested: 'Promo Code Requested',
    yourPromocode: 'Your Promocode!',
    yourPromocodeLabel: 'Your Promocode:',
    promocodeDescription: "Here's your exclusive promocode. Copy it and use it at checkout!",
    promocodeTipTitle: 'Tip:',
    promocodeTipDescription: 'Save this code! You can only select one advertisement per session.',
    copyCode: 'Copy Code',
    copied: 'Copied!',
    close: 'Close',
    visitPartnerWebsite: 'Visit Partner Website',
    useYourCode: 'Use Your Code →',
    visitPartnerPage: "Visit the partner's page to redeem your code",
    apiRequestSentTitle: 'API Request Sent',
    apiRequestSentDescription: 'Your promocode request has been sent to the provider. You will receive your code via email shortly.',
    apiRequestSentNoteTitle: 'Note:',
    apiRequestSentNoteDescription: 'Please check your email inbox (and spam folder) for your personalized promocode.',
    apiRequestSentButton: 'Got it!',
    termsOfService: 'Terms of Service',
    contactUs: 'Contact Us',
    poweredBy: 'This service is powered by Magic Link',
    campaign: 'Campaign',
    region: 'Region',
    noActiveOffers: 'No Active Offers',
    noActiveOffersDescription: 'This campaign currently has no active offers to display.',
    campaignNotFound: 'Campaign Not Found',
    campaignNotFoundDescription: "The campaign you're looking for doesn't exist or is no longer active.",
    loadingCampaign: 'Loading campaign...',
    redirectDescription: 'Click "Get Promo Code" and you\'ll be instantly redirected to the provider\'s page where you can claim your offer.',
    directContactNotification: 'Thank you for your choice! You will be contacted soon by the advertisement provider via your provided email.',
    directContactTitle: 'Direct contact confirmation',
    directContactConfirmQuestion: 'Do you want to proceed?',
    redirectConfirmTitle: 'Redirect confirmation',
    redirectConfirmMessage: 'You will be redirected to the advertisement provider\'s website.',
    redirectConfirmQuestion: 'Do you want to continue?',
    proceed: 'Proceed',
    promoSentEmailTitle: 'Promo code sent',
    promoSentEmailDescription: 'We emailed the promo code to {email}. Please check your inbox (and spam folder).',
    promoEmailReminderMessage: 'To avoid losing it, we also emailed the code to you.',
    privacyConsentReminderTitle: 'Privacy consent required',
    privacyConsentReminderMessage: 'Please agree to the Privacy Policy to continue.',
    invalidEmailTitle: 'Invalid email address',
    invalidEmailMessage: 'Please enter a valid email address to continue.',
    missingEmailTitle: 'Email required',
    missingEmailMessage: 'Please enter your email address to continue.',
    prefetchBadge: 'Preparing your personalized view',
    prefetchTitle: 'We are defining the number of offers available to you',
    prefetchOffersReady: 'Offers unlocked for you',
    prefetchOffersPending: 'Potential offers just for you',
    prefetchProgressLabel: '{percent}% complete',
    prefetchButtonReady: 'Go to offers',
    prefetchButtonPending: 'Preparing...',
    newsletterTitle: 'Want to be up to date with our offers?',
    newsletterPlaceholder: 'Enter your email',
    newsletterPrivacyLabel: 'I agree with the Privacy Policy',
    newsletterSubscribe: 'Subscribe',
    newsletterSuccess: 'Thank you for subscribing!',
    newsletterError: 'Failed to subscribe. Please try again.'
  },

  // Bulgarian
  'bg': {
    featuredOffers: 'Препоръчани оферти',
    featuredOffersDescription: 'Открийте невероятни оферти и предложения от нашите доверени партньори',
    exclusiveOffersTitle: 'Вашите ексклузивни оферти ви очакват',
    exclusiveOffersDescription: 'Кликнете на всяка оферта по-долу, за да получите специалния си подарък от нашите доверени партньори',
    howToGetGift: 'Как да получите подаръка си - 3 лесни стъпки',
    step1Title: 'Изберете оферта',
    step1Description: 'Разгледайте и изберете любимата си оферта',
    step2Title: 'Въведете имейл',
    step2Description: 'Предоставете вашия имейл адрес',
    step3Title: 'Получете код',
    step3Description: 'Получете промо кода си незабавно',
    filterByCategories: 'Филтриране по категории',
    swipeHint: '← Плъзнете, за да видите други категории →',
    clearFilter: 'Изчисти филтър',
    categories: 'Категории:',
    validUntil: 'Валидно до',
    getPromoCode: 'Вземете промо код',
    requestCode: 'Заявете код',
    cancel: 'Отказ',
    emailAddress: 'Имейл адрес',
    enterYourEmail: 'Въведете вашия имейл',
    agreeWithPrivacy: 'Съгласен съм с',
    privacyPolicy: 'Политика за поверителност',
    platformPrivacyPolicy: 'Политика за поверителност на платформата',
    partnerPrivacyPolicy: 'Политика за поверителност на партньора',
    and: 'и',
    promoCodeRequested: 'Промо кодът е заявен',
    yourPromocode: 'Вашият промо код!',
    yourPromocodeLabel: 'Вашият промо код:',
    promocodeDescription: 'Ето вашия ексклузивен промо код. Копирайте го и го използвайте при плащане!',
    promocodeTipTitle: 'Съвет:',
    promocodeTipDescription: 'Запазете този код! Можете да изберете само една реклама на сесия.',
    copyCode: 'Копирай код',
    copied: 'Копирано!',
    close: 'Затвори',
    visitPartnerWebsite: 'Посетете сайта на партньора',
    useYourCode: 'Използвай кода →',
    visitPartnerPage: 'Посетете страницата на партньора, за да използвате кода си',
    apiRequestSentTitle: 'Заявката към API е изпратена',
    apiRequestSentDescription: 'Вашата заявка за промо код беше изпратена към доставчика. Скоро ще получите кода си по имейл.',
    apiRequestSentNoteTitle: 'Бележка:',
    apiRequestSentNoteDescription: 'Моля, проверете пощата си (и спам папката) за персонализирания промо код.',
    apiRequestSentButton: 'Разбрах!',
    termsOfService: 'Условия за ползване',
    contactUs: 'Свържете се с нас',
    poweredBy: 'Този портал се предоставя от Magic Links',
    campaign: 'Кампания',
    region: 'Регион',
    noActiveOffers: 'Няма активни оферти',
    noActiveOffersDescription: 'Тази кампания в момента няма активни оферти за показване.',
    campaignNotFound: 'Кампанията не е намерена',
    campaignNotFoundDescription: 'Кампанията, която търсите, не съществува или вече не е активна.',
    loadingCampaign: 'Зареждане на кампания...',
    redirectDescription: 'Кликнете "Вземете промо код" и ще бъдете незабавно пренасочени към страницата на доставчика, където можете да получите офертата си.',
    directContactNotification: 'Благодаря за вашия избор! Скоро ще бъдете свързани от доставчика на рекламата чрез предоставения имейл.',
    directContactTitle: 'Потвърждение за директен контакт',
    directContactConfirmQuestion: 'Желаете ли да продължите?',
    redirectConfirmTitle: 'Потвърждение за пренасочване',
    redirectConfirmMessage: 'Ще бъдете пренасочени към уебсайта на рекламодателя.',
    redirectConfirmQuestion: 'Желаете ли да продължите?',
    proceed: 'Продължи',
    promoSentEmailTitle: 'Промо кодът е изпратен',
    promoSentEmailDescription: 'Изпратихме промо кода на {email}. Проверете пощата си (и спам папката).',
    promoEmailReminderMessage: 'За да не го загубите, изпратихме кода и на вашия имейл.',
    privacyConsentReminderTitle: 'Необходимо е съгласие за поверителност',
    privacyConsentReminderMessage: 'Моля, съгласете се с Политиката за поверителност, за да продължите.',
    invalidEmailTitle: 'Невалиден имейл адрес',
    invalidEmailMessage: 'Моля, въведете валиден имейл адрес, за да продължите.',
    missingEmailTitle: 'Необходим е имейл адрес',
    missingEmailMessage: 'Моля, въведете своя имейл адрес, за да продължите.',
    prefetchBadge: 'Подготвяме вашия персонализиран изглед',
    prefetchTitle: 'Определяме броя на офертите, достъпни за вас',
    prefetchOffersReady: 'Офертите са отключени за вас',
    prefetchOffersPending: 'Възможни оферти специално за вас',
    prefetchProgressLabel: '{percent}% завършено',
    prefetchButtonReady: 'Към офертите',
    prefetchButtonPending: 'Подготвяме...',
    newsletterTitle: 'Искате ли да сте в крак с нашите оферти?',
    newsletterPlaceholder: 'Въведете вашия имейл',
    newsletterPrivacyLabel: 'Съгласен съм с Политиката за поверителност',
    newsletterSubscribe: 'Абонирай се',
    newsletterSuccess: 'Благодарим ви за абонамента!',
    newsletterError: 'Неуспешен абонамент. Моля, опитайте отново.'
  },

  // English (US)
  'en-US': {
    featuredOffers: 'Featured Offers',
    featuredOffersDescription: 'Discover amazing deals and offers from our trusted partners',
    exclusiveOffersTitle: 'Your Exclusive Offers Await',
    exclusiveOffersDescription: 'Click on any offer below to claim your special gift from our trusted partners',
    howToGetGift: 'How to Get Your Gift - 3 Easy Steps',
    step1Title: 'Choose Offer',
    step1Description: 'Browse and select your favorite deal',
    step2Title: 'Enter Email',
    step2Description: 'Provide your email address',
    step3Title: 'Get Code',
    step3Description: 'Receive your promo code instantly',
    filterByCategories: 'Filter by Categories',
    swipeHint: '← Swipe to see more →',
    clearFilter: 'Clear Filter',
    categories: 'Categories:',
    validUntil: 'Valid until',
    getPromoCode: 'Get Promo Code',
    requestCode: 'Request Code',
    cancel: 'Cancel',
    emailAddress: 'Email Address',
    enterYourEmail: 'Enter your email',
    agreeWithPrivacy: 'I agree with',
    privacyPolicy: 'Privacy Policy',
    platformPrivacyPolicy: 'Platform Privacy Policy',
    partnerPrivacyPolicy: 'Partner Privacy Policy',
    and: 'and',
    promoCodeRequested: 'Promo Code Requested',
    yourPromocode: 'Your Promocode!',
    yourPromocodeLabel: 'Your Promocode:',
    promocodeDescription: "Here's your exclusive promocode. Copy it and use it at checkout!",
    promocodeTipTitle: 'Tip:',
    promocodeTipDescription: 'Save this code! You can only select one advertisement per session.',
    copyCode: 'Copy Code',
    copied: 'Copied!',
    close: 'Close',
    visitPartnerWebsite: 'Visit Partner Website',
    useYourCode: 'Use Your Code →',
    visitPartnerPage: "Visit the partner's page to redeem your code",
    apiRequestSentTitle: 'API Request Sent',
    apiRequestSentDescription: 'Your promocode request has been sent to the provider. You will receive your code via email shortly.',
    apiRequestSentNoteTitle: 'Note:',
    apiRequestSentNoteDescription: 'Please check your email inbox (and spam folder) for your personalized promocode.',
    apiRequestSentButton: 'Got it!',
    termsOfService: 'Terms of Service',
    contactUs: 'Contact Us',
    poweredBy: 'This service is powered by Magic Link',
    campaign: 'Campaign',
    region: 'Region',
    noActiveOffers: 'No Active Offers',
    noActiveOffersDescription: 'This campaign currently has no active offers to display.',
    campaignNotFound: 'Campaign Not Found',
    campaignNotFoundDescription: "The campaign you're looking for doesn't exist or is no longer active.",
    loadingCampaign: 'Loading campaign...',
    redirectDescription: 'Click "Get Promo Code" and you\'ll be instantly redirected to the provider\'s page where you can claim your offer.',
    directContactNotification: 'Thank you for your choice! You will be contacted soon by the advertisement provider via your provided email.',
    directContactTitle: 'Direct contact confirmation',
    directContactConfirmQuestion: 'Do you want to proceed?',
    redirectConfirmTitle: 'Redirect confirmation',
    redirectConfirmMessage: 'You will be redirected to the advertisement provider\'s website.',
    redirectConfirmQuestion: 'Do you want to continue?',
    proceed: 'Proceed',
    promoSentEmailTitle: 'Promo code sent',
    promoSentEmailDescription: 'We emailed the promo code to {email}. Please check your inbox (and spam folder).',
    promoEmailReminderMessage: 'To avoid losing it, we also emailed the code to you.',
    privacyConsentReminderTitle: 'Privacy consent required',
    privacyConsentReminderMessage: 'Please agree to the Privacy Policy to continue.',
    invalidEmailTitle: 'Invalid email address',
    invalidEmailMessage: 'Please enter a valid email address to continue.',
    missingEmailTitle: 'Email required',
    missingEmailMessage: 'Please enter your email address to continue.',
    prefetchBadge: 'Preparing your personalized view',
    prefetchTitle: 'We are defining the number of offers available to you',
    prefetchOffersReady: 'Offers unlocked for you',
    prefetchOffersPending: 'Potential offers just for you',
    prefetchProgressLabel: '{percent}% complete',
    prefetchButtonReady: 'Go to offers',
    prefetchButtonPending: 'Preparing...',
    newsletterTitle: 'Want to be up to date with our offers?',
    newsletterPlaceholder: 'Enter your email',
    newsletterPrivacyLabel: 'I agree with the Privacy Policy',
    newsletterSubscribe: 'Subscribe',
    newsletterSuccess: 'Thank you for subscribing!',
    newsletterError: 'Failed to subscribe. Please try again.'
  },

  // English (UK)
  'en-GB': {
    featuredOffers: 'Featured Offers',
    featuredOffersDescription: 'Discover amazing deals and offers from our trusted partners',
    exclusiveOffersTitle: 'Your Exclusive Offers Await',
    exclusiveOffersDescription: 'Click on any offer below to claim your special gift from our trusted partners',
    howToGetGift: 'How to Get Your Gift - 3 Easy Steps',
    step1Title: 'Choose Offer',
    step1Description: 'Browse and select your favourite deal',
    step2Title: 'Enter Email',
    step2Description: 'Provide your email address',
    step3Title: 'Get Code',
    step3Description: 'Receive your promo code instantly',
    filterByCategories: 'Filter by Categories',
    swipeHint: '← Swipe to see more →',
    clearFilter: 'Clear Filter',
    categories: 'Categories:',
    validUntil: 'Valid until',
    getPromoCode: 'Get Promo Code',
    requestCode: 'Request Code',
    cancel: 'Cancel',
    emailAddress: 'Email Address',
    enterYourEmail: 'Enter your email',
    agreeWithPrivacy: 'I agree with',
    privacyPolicy: 'Privacy Policy',
    platformPrivacyPolicy: 'Platform Privacy Policy',
    partnerPrivacyPolicy: 'Partner Privacy Policy',
    and: 'and',
    promoCodeRequested: 'Promo Code Requested',
    yourPromocode: 'Your Promocode!',
    yourPromocodeLabel: 'Your Promocode:',
    promocodeDescription: "Here's your exclusive promocode. Copy it and use it at checkout!",
    promocodeTipTitle: 'Tip:',
    promocodeTipDescription: 'Save this code! You can only select one advertisement per session.',
    copyCode: 'Copy Code',
    copied: 'Copied!',
    close: 'Close',
    visitPartnerWebsite: 'Visit Partner Website',
    useYourCode: 'Use Your Code →',
    visitPartnerPage: "Visit the partner's page to redeem your code",
    apiRequestSentTitle: 'API Request Sent',
    apiRequestSentDescription: 'Your promocode request has been sent to the provider. You will receive your code via email shortly.',
    apiRequestSentNoteTitle: 'Note:',
    apiRequestSentNoteDescription: 'Please check your email inbox (and spam folder) for your personalized promocode.',
    apiRequestSentButton: 'Got it!',
    termsOfService: 'Terms of Service',
    contactUs: 'Contact Us',
    poweredBy: 'This service is powered by Magic Link',
    campaign: 'Campaign',
    region: 'Region',
    noActiveOffers: 'No Active Offers',
    noActiveOffersDescription: 'This campaign currently has no active offers to display.',
    campaignNotFound: 'Campaign Not Found',
    campaignNotFoundDescription: "The campaign you're looking for doesn't exist or is no longer active.",
    loadingCampaign: 'Loading campaign...',
    redirectDescription: 'Click "Get Promo Code" and you\'ll be instantly redirected to the provider\'s page where you can claim your offer.',
    directContactNotification: 'Thank you for your choice! You will be contacted soon by the advertisement provider via your provided email.',
    directContactTitle: 'Direct contact confirmation',
    directContactConfirmQuestion: 'Do you want to proceed?',
    redirectConfirmTitle: 'Redirect confirmation',
    redirectConfirmMessage: 'You will be redirected to the advertisement provider\'s website.',
    redirectConfirmQuestion: 'Do you want to continue?',
    proceed: 'Proceed',
    promoSentEmailTitle: 'Promo code sent',
    promoSentEmailDescription: 'We emailed the promo code to {email}. Please check your inbox (and spam folder).',
    promoEmailReminderMessage: 'To avoid losing it, we also emailed the code to you.',
    privacyConsentReminderTitle: 'Privacy consent required',
    privacyConsentReminderMessage: 'Please agree to the Privacy Policy to continue.',
    invalidEmailTitle: 'Invalid email address',
    invalidEmailMessage: 'Please enter a valid email address to continue.',
    missingEmailTitle: 'Email required',
    missingEmailMessage: 'Please enter your email address to continue.',
    prefetchBadge: 'Preparing your personalised view',
    prefetchTitle: 'We are defining the number of offers available to you',
    prefetchOffersReady: 'Offers unlocked for you',
    prefetchOffersPending: 'Potential offers just for you',
    prefetchProgressLabel: '{percent}% complete',
    prefetchButtonReady: 'Go to offers',
    prefetchButtonPending: 'Preparing...',
    newsletterTitle: 'Want to be up to date with our offers?',
    newsletterPlaceholder: 'Enter your email',
    newsletterPrivacyLabel: 'I agree with the Privacy Policy',
    newsletterSubscribe: 'Subscribe',
    newsletterSuccess: 'Thank you for subscribing!',
    newsletterError: 'Failed to subscribe. Please try again.'
  },

  // Spanish (US)
  'es-US': {
    featuredOffers: 'Ofertas destacadas',
    featuredOffersDescription: 'Descubre increíbles ofertas y promociones de nuestros socios de confianza',
    exclusiveOffersTitle: 'Tus ofertas exclusivas te esperan',
    exclusiveOffersDescription: 'Haz clic en cualquier oferta a continuación para reclamar tu regalo especial de nuestros socios de confianza',
    howToGetGift: 'Cómo obtener tu regalo - 3 pasos fáciles',
    step1Title: 'Elige oferta',
    step1Description: 'Navega y selecciona tu oferta favorita',
    step2Title: 'Ingresa email',
    step2Description: 'Proporciona tu dirección de correo',
    step3Title: 'Obtén código',
    step3Description: 'Recibe tu código promocional al instante',
    filterByCategories: 'Filtrar por categorías',
    swipeHint: '← Desliza para ver más →',
    clearFilter: 'Borrar filtro',
    categories: 'Categorías:',
    validUntil: 'Válido hasta',
    getPromoCode: 'Obtener código promocional',
    requestCode: 'Solicitar código',
    cancel: 'Cancelar',
    emailAddress: 'Dirección de correo electrónico',
    enterYourEmail: 'Ingrese su correo electrónico',
    agreeWithPrivacy: 'Estoy de acuerdo con',
    privacyPolicy: 'Política de privacidad',
    platformPrivacyPolicy: 'Política de privacidad de la plataforma',
    partnerPrivacyPolicy: 'Política de privacidad del socio',
    and: 'y',
    promoCodeRequested: 'Código promocional solicitado',
    yourPromocode: '¡Tu código promocional!',
    yourPromocodeLabel: 'Tu código promocional:',
    promocodeDescription: '¡Aquí está tu código promocional exclusivo. Cópialo y úsalo al pagar!',
    promocodeTipTitle: 'Consejo:',
    promocodeTipDescription: '¡Guarda este código! Solo puedes seleccionar un anuncio por sesión.',
    copyCode: 'Copiar código',
    copied: '¡Copiado!',
    close: 'Cerrar',
    visitPartnerWebsite: 'Visitar sitio del socio',
    useYourCode: 'Usa tu código →',
    visitPartnerPage: 'Visita la página del socio para canjear tu código',
    apiRequestSentTitle: 'Solicitud API enviada',
    apiRequestSentDescription: 'Tu solicitud de código promocional se ha enviado al proveedor. Recibirás tu código por correo electrónico en breve.',
    apiRequestSentNoteTitle: 'Nota:',
    apiRequestSentNoteDescription: 'Revisa tu bandeja de entrada (y correo no deseado) para tu código promocional personalizado.',
    apiRequestSentButton: '¡Entendido!',
    termsOfService: 'Términos de servicio',
    contactUs: 'Contáctenos',
    poweredBy: 'Este servicio es proporcionado por Magic Link',
    campaign: 'Campaña',
    region: 'Región',
    noActiveOffers: 'No hay ofertas activas',
    noActiveOffersDescription: 'Esta campaña actualmente no tiene ofertas activas para mostrar.',
    campaignNotFound: 'Campaña no encontrada',
    campaignNotFoundDescription: 'La campaña que buscas no existe o ya no está activa.',
    loadingCampaign: 'Cargando campaña...',
    redirectDescription: 'Haz clic en "Obtener código promocional" y serás redirigido instantáneamente a la página del proveedor donde podrás reclamar tu oferta.',
    directContactNotification: '¡Gracias por tu elección! Serás contactado pronto por el proveedor del anuncio a través del correo electrónico proporcionado.',
    directContactTitle: 'Confirmación de contacto directo',
    directContactConfirmQuestion: '¿Deseas continuar?',
    redirectConfirmTitle: 'Confirmación de redireccionamiento',
    redirectConfirmMessage: 'Serás redirigido al sitio web del proveedor del anuncio.',
    redirectConfirmQuestion: '¿Deseas continuar?',
    proceed: 'Continuar',
    promoSentEmailTitle: '¡Código enviado!',
    promoSentEmailDescription: 'Hemos enviado el código promocional a {email}. Revisa tu bandeja de entrada (y spam).',
    promoEmailReminderMessage: 'Para no perderlo, también te enviamos el código por correo electrónico.',
    privacyConsentReminderTitle: 'Se requiere consentimiento de privacidad',
    privacyConsentReminderMessage: 'Acepta la Política de Privacidad para continuar.',
    invalidEmailTitle: 'Correo electrónico inválido',
    invalidEmailMessage: 'Ingresa un correo electrónico válido para continuar.',
    missingEmailTitle: 'Correo electrónico requerido',
    missingEmailMessage: 'Ingresa tu correo electrónico para continuar.',
    prefetchBadge: 'Preparando tu vista personalizada',
    prefetchTitle: 'Estamos definiendo cuántas ofertas tienes disponibles',
    prefetchOffersReady: 'Ofertas desbloqueadas para ti',
    prefetchOffersPending: 'Ofertas potenciales solo para ti',
    prefetchProgressLabel: '{percent}% completado',
    prefetchButtonReady: 'Ir a las ofertas',
    prefetchButtonPending: 'Preparando...',
    newsletterTitle: '¿Quieres estar al día con nuestras ofertas?',
    newsletterPlaceholder: 'Ingresa tu correo electrónico',
    newsletterPrivacyLabel: 'Acepto la Política de Privacidad',
    newsletterSubscribe: 'Suscribirse',
    newsletterSuccess: '¡Gracias por suscribirte!',
    newsletterError: 'Error al suscribirse. Por favor, inténtalo de nuevo.'
  },

  // French (Canada)
  'fr-CA': {
    featuredOffers: 'Offres en vedette',
    featuredOffersDescription: 'Découvrez des offres et des promotions incroyables de nos partenaires de confiance',
    exclusiveOffersTitle: 'Vos offres exclusives vous attendent',
    exclusiveOffersDescription: 'Cliquez sur n\'importe quelle offre ci-dessous pour réclamer votre cadeau spécial de nos partenaires de confiance',
    howToGetGift: 'Comment obtenir votre cadeau - 3 étapes faciles',
    step1Title: 'Choisissez une offre',
    step1Description: 'Parcourez et sélectionnez votre offre préférée',
    step2Title: 'Entrez votre courriel',
    step2Description: 'Fournissez votre adresse de courriel',
    step3Title: 'Obtenez votre code',
    step3Description: 'Recevez votre code promotionnel instantanément',
    filterByCategories: 'Filtrer par catégories',
    swipeHint: '← Faites glisser pour voir plus →',
    clearFilter: 'Effacer le filtre',
    categories: 'Catégories :',
    validUntil: 'Valide jusqu\'au',
    getPromoCode: 'Obtenir le code promotionnel',
    requestCode: 'Demander le code',
    cancel: 'Annuler',
    emailAddress: 'Adresse de courriel',
    enterYourEmail: 'Entrez votre courriel',
    agreeWithPrivacy: 'Je suis d\'accord avec',
    privacyPolicy: 'Politique de confidentialité',
    platformPrivacyPolicy: 'Politique de confidentialité de la plateforme',
    partnerPrivacyPolicy: 'Politique de confidentialité du partenaire',
    and: 'et',
    promoCodeRequested: 'Code promotionnel demandé',
    yourPromocode: 'Votre code promotionnel !',
    yourPromocodeLabel: 'Votre code promotionnel :',
    promocodeDescription: 'Voici votre code promotionnel exclusif. Copiez-le et utilisez-le lors du paiement !',
    promocodeTipTitle: 'Conseil :',
    promocodeTipDescription: 'Gardez ce code ! Vous ne pouvez sélectionner qu\'une seule publicité par session.',
    copyCode: 'Copier le code',
    copied: 'Copié !',
    close: 'Fermer',
    visitPartnerWebsite: 'Visiter le site du partenaire',
    useYourCode: 'Utiliser votre code →',
    visitPartnerPage: 'Visitez la page du partenaire pour utiliser votre code',
    apiRequestSentTitle: 'Demande API envoyée',
    apiRequestSentDescription: 'Votre demande de code promotionnel a été envoyée au fournisseur. Vous recevrez votre code par courriel sous peu.',
    apiRequestSentNoteTitle: 'Note :',
    apiRequestSentNoteDescription: 'Veuillez vérifier votre boîte de réception (et le dossier de courriels indésirables) pour votre code promotionnel personnalisé.',
    apiRequestSentButton: 'Compris !',
    termsOfService: 'Conditions de service',
    contactUs: 'Nous contacter',
    poweredBy: 'Ce service est fourni par Magic Link',
    campaign: 'Campagne',
    region: 'Région',
    noActiveOffers: 'Aucune offre active',
    noActiveOffersDescription: 'Cette campagne n\'a actuellement aucune offre active à afficher.',
    campaignNotFound: 'Campagne non trouvée',
    campaignNotFoundDescription: 'La campagne que vous recherchez n\'existe pas ou n\'est plus active.',
    loadingCampaign: 'Chargement de la campagne...',
    redirectDescription: 'Cliquez sur « Obtenir le code promotionnel » et vous serez redirigé instantanément vers la page du fournisseur où vous pourrez réclamer votre offre.',
    directContactNotification: 'Merci de votre choix ! Vous serez contacté sous peu par le fournisseur de la publicité via le courriel que vous avez fourni.',
    directContactTitle: 'Confirmation de contact direct',
    directContactConfirmQuestion: 'Voulez-vous continuer ?',
    redirectConfirmTitle: 'Confirmation de redirection',
    redirectConfirmMessage: 'Vous serez redirigé vers le site web du fournisseur de la publicité.',
    redirectConfirmQuestion: 'Voulez-vous continuer ?',
    proceed: 'Continuer',
    promoSentEmailTitle: 'Code promotionnel envoyé',
    promoSentEmailDescription: 'Nous avons envoyé le code promotionnel à {email}. Veuillez vérifier votre boîte de réception (et le dossier de courriels indésirables).',
    promoEmailReminderMessage: 'Pour éviter de le perdre, nous vous avons également envoyé le code par courriel.',
    privacyConsentReminderTitle: 'Consentement à la confidentialité requis',
    privacyConsentReminderMessage: 'Veuillez accepter la politique de confidentialité pour continuer.',
    invalidEmailTitle: 'Adresse de courriel invalide',
    invalidEmailMessage: 'Veuillez entrer une adresse de courriel valide pour continuer.',
    missingEmailTitle: 'Courriel requis',
    missingEmailMessage: 'Veuillez entrer votre adresse de courriel pour continuer.',
    prefetchBadge: 'Nous préparons votre vue personnalisée',
    prefetchTitle: 'Nous définissons le nombre d’offres disponibles pour vous',
    prefetchOffersReady: 'Offres débloquées pour vous',
    prefetchOffersPending: 'Offres potentielles rien que pour vous',
    prefetchProgressLabel: '{percent}% terminé',
    prefetchButtonReady: 'Voir les offres',
    prefetchButtonPending: 'Préparation...',
    newsletterTitle: 'Voulez-vous être au courant de nos offres?',
    newsletterPlaceholder: 'Entrez votre courriel',
    newsletterPrivacyLabel: 'J\'accepte la Politique de confidentialité',
    newsletterSubscribe: 'S\'abonner',
    newsletterSuccess: 'Merci de vous être abonné!',
    newsletterError: 'Échec de l\'abonnement. Veuillez réessayer.'
  }
};

// Category translations
export const categoryTranslations: Record<string, Record<string, string>> = {
  'en': {
    'Electronics': 'Electronics',
    'Banking': 'Banking',
    'Education': 'Education',
    'Entertainment': 'Entertainment',
    'Clothes and footwear': 'Clothes and footwear',
    'Sport': 'Sport',
    'Beauty and health': 'Beauty and health',
    'Auto': 'Auto',
    'For children': 'For children',
    'Hotels': 'Hotels',
    'Gambling' : 'Gambling',
    'Games' : 'Games', 
    'Movies and Music' : 'Movies and Music',
    'Others' : 'Others',
    'Books' : 'Books', 
    'Household goods' : 'Household goods',
    'Travel': 'Travel', 
    'For women' : 'For women',
    'For men' : 'For men'

  },
  'bg': {
    'Electronics': 'Електроника',
    'Banking': 'Банкиране',
    'Education': 'Образование',
    'Entertainment': 'Развлечения',
    'Clothes and footwear': 'Дрехи и обувки',
    'Sport': 'Спорт',
    'Beauty and health': 'Красота и здраве',
    'Auto': 'Авто',
    'For children': 'За деца',
    'Hotels': 'Хотели',
    'Gambling' : 'Хазарт',
    'Games' : 'Игри', 
    'Movies and Music' : 'Филми и музика',
    'Others' : 'Други',
    'Books' : 'Книги', 
    'Household goods' : 'Домакрински стоки',
    'Travel':  'Пътуване', 
    'For women' : 'За жени',
    'For men' : 'За мъже'
  },
  'es-US': {
    'Electronics': 'Electrónica',
    'Banking': 'Banca',
    'Education': 'Educación',
    'Entertainment': 'Entretenimiento',
    'Clothes and footwear': 'Ropa y calzado',
    'Sport': 'Deporte',
    'Beauty and health': 'Belleza y salud',
    'Auto': 'Automóviles',
    'For children': 'Para niños',
    'Hotels': 'Hoteles',
    'Gambling' : 'Gambling',
    'Games' : 'Games', 
    'Movies and Music' : 'Movies and Music',
    'Others' : 'Others',
    'Books' : 'Books', 
    'Household goods' : 'Household goods',
    'Travel': 'Travel', 
    'For women' : 'For women',
    'For men' : 'For men'
  },
  'fr-CA': {
    'Electronics': 'Électronique',
    'Banking': 'Banque',
    'Education': 'Éducation',
    'Entertainment': 'Divertissement',
    'Clothes and footwear': 'Vêtements et chaussures',
    'Sport': 'Sport',
    'Beauty and health': 'Beauté et santé',
    'Auto': 'Automobile',
    'For children': 'Pour enfants',
    'Hotels': 'Hôtels',
  },
};

function resolveLocaleEntry<T>(map: Record<string, T>, locale?: string): T | undefined {
  if (!locale) return undefined;

  if (map[locale]) {
    return map[locale];
  }

  const normalizedLocale = locale.toLowerCase();
  if (normalizedLocale !== locale && map[normalizedLocale]) {
    return map[normalizedLocale];
  }

  const languageCode = locale.split('-')[0];
  if (map[languageCode]) {
    return map[languageCode];
  }

  const normalizedLanguageCode = languageCode.toLowerCase();
  if (normalizedLanguageCode !== languageCode && map[normalizedLanguageCode]) {
    return map[normalizedLanguageCode];
  }

  return undefined;
}

// Helper function to get translations
export function getTranslations(locale?: string): Translations {
  return resolveLocaleEntry(translations, locale) || translations['en'];
}

// Helper function to get category translation
export function getCategoryTranslation(category: string, locale?: string): string {
  const categoryMap = resolveLocaleEntry(categoryTranslations, locale) || categoryTranslations['en'];
  
  return categoryMap[category] || category;
}

// Region to locale mapping
export const regionToLocale: Record<string, string> = {
  'BGR': 'bg',      // Bulgaria
  'USA': 'en-US',   // United States
  'GBR': 'en-GB',   // United Kingdom
  'CAN': 'en-CA',   // Canada (default English, can be fr-CA)
  'FRA': 'fr-FR',   // France
  'DEU': 'de-DE',   // Germany
  'ESP': 'es-ES',   // Spain
};

// Get locale from region code
export function getLocaleFromRegion(region: string): string {
  if (!region) return 'en';

  const trimmed = region.trim();
  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();

  return (
    regionToLocale[trimmed] ||
    regionToLocale[upper] ||
    regionToLocale[lower] ||
    'en'
  );
}
